/* eslint-disable prefer-template */
/* eslint-disable no-param-reassign */
import SearchStorage from '@Services/SearchStorage';
import { replaceCitations } from '@Utils/String';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import UserDatabases from '@Services/UserDatabases';
import AppStorage from '@Services/AppStorage';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import HistoryResource from '@Services/HistoryResource';
import HtmlStripsService from '@Services/HtmlStripsService';
import moment from 'moment-timezone';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import each from 'lodash/each';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import groupBy from 'lodash/groupBy';
import toArray from 'lodash/toArray';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import { SearchTypes } from '@Pages/History/Constants';
import appHistory from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';

const boundOperators = {
  op_num_lesseq: '<=',
  op_num_equal: '=',
  op_num_more: '>',
  op_num_moreeq: '>=',
  op_num_less: '<',
  op_txt_is: 'is',
  op_num_between: 'between',
  op_txt_contains: 'contains',
  op_txt_starts_with: 'starts with',
  op_txt_ends_with: 'ends with',
};

function defineQuickSearchDescription(searchItem) {
  if (!isUndefined(searchItem.quickSearchItem.content) && searchItem.quickSearchItem.content !== '') {
    searchItem.comment = `${searchItem.comment}"${searchItem.quickSearchItem.content}"`;
    if (!isUndefined(searchItem.quickSearchItem.structure) && searchItem.quickSearchItem.structure !== '') {
      searchItem.comment += ' AND ';
    }
  }
}

function defineFilteredDescription(searchItem) {
  const appliedFilters = [];

  forEach(searchItem.filters, (filterVal) => {
    if (filterVal.property === 'YY') {
      appliedFilters.push('Structure');
    } else if (filterVal.property === 'RXD.STP') {
      appliedFilters.push('Single step reactions only');
    } else if (appliedFilters.indexOf(filterVal.title) < 0) {
      appliedFilters.push(filterVal.title);
    }
  });

  searchItem.comment += appliedFilters.join(', ');
}

function decamelize(str) {
  return str.replace(/(?:^\w|[A-Z])/g, (letter, index) => {
    return index === 0 ? letter.toUpperCase() : ` ${letter}`;
  });
}
function capitalize(input) {
  if (isString(input)) {
    return decamelize(input);
  }
  return input;
}

function defineContextSwitchDescription(searchItem) {
  if (searchItem.previousResult.resultSize) {
    searchItem.comment = `${searchItem.comment}${searchItem.previousResult.resultSize} `;
  }

  if (searchItem.previousResult.context) {
    const context = replaceCitations(searchItem.previousResult.context);

    searchItem.comment += capitalize(context);
  }
}

export function defineFactDescription(searchItem, factItem) {
  // eslint-disable-next-line no-use-before-define
  searchItem.comment = composeFactDescription(searchItem.comment, factItem);
}

export function extractStructuresFromFacts(facts) {
  let structures = [];

  eachHierarchy(
    facts,
    (node) => {
      if (node.structures) {
        structures = structures.concat(node.structures);
      }
    },
    null,
    { children: 'facts' }
  );

  return structures;
}

function _getStructureFromSearch(search, context) {
  if (context && search[context] && search[context].structure) {
    return search[context].structure;
  }
  return search.structure;
}

export function updateItemsComments(databaseStorage) {
  forEach(databaseStorage, (database) => {
    forEach(database.items, (item) => {
      item.comment = HtmlStripsService.getTitleWithoutHtml(item.comment, 'full');
    });
  });
}

export function splitHistoryByDBs(historyItems) {
  const groupedItems = groupBy(historyItems, 'databaseId');
  const formatteditems =
    Object.keys(groupedItems).length > 0 &&
    Object.keys(groupedItems).map((eachKey) => {
      const name = UserDatabases.getDbName(eachKey);
      return name
        ? {
            databaseId: eachKey,
            priority: UserDatabases.isDefaultDatabase(eachKey) ? '' : name.toLowerCase(),
            databaseName: name,
            items: [...groupedItems[eachKey]],
          }
        : null;
    });
  return sortBy(compact(toArray(formatteditems)), 'priority');
}

export function formatSavedHistoryData(savedHistoryData) {
  savedHistoryData.forEach((item) => {
    if (item.queryFactItem) {
      item.structures = extractStructuresFromFacts(item.queryFactItem);
    }
  });
  return splitHistoryByDBs(savedHistoryData);
}

function setQBSearch(search, historyTypes, historyItem, payload) {
  historyItem.queryFactItem = search.queryFactItem;
  historyItem.historyType = historyTypes.QUERY_BUILDER;
  if (payload) {
    payload.queryFactItem = search.queryFactItem;
    payload.historyType = historyTypes.QUERY_BUILDER;
  }
}

function setSimilaritySearch(search, historyItem, payload) {
  historyItem.structures = _getStructureFromSearch(search, historyItem.context);
  historyItem.queryFactItem = search.queryFactItem;
  historyItem.historyType = SearchTypes.SIMILARITY_SEARCH;
  if (payload) {
    payload.structures = _getStructureFromSearch(search, payload.context);
    payload.queryFactItem = search.queryFactItem;
    payload.historyType = SearchTypes.SIMILARITY_SEARCH;
  }
}

function setMarkushSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.MARKUSH_SEARCH;
  if (payload) {
    payload.historyType = SearchTypes.MARKUSH_SEARCH;
  }
}

function setMarkushSubstancesSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.MARKUSH_SUBSTANCES;
  if (payload) {
    payload.historyType = SearchTypes.MARKUSH_SUBSTANCES;
  }
}

function setManuallyFiltered(historyItem, payload) {
  historyItem.historyType = SearchTypes.MANUALLY_FILTERED;
  if (payload) {
    payload.historyType = SearchTypes.MANUALLY_FILTERED;
  }
}

function setCrossLinkingSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.CROSS_LINKING;
  if (payload) {
    payload.historyType = SearchTypes.CROSS_LINKING;
  }
}

function setManualSynthesis(historyItem, payload) {
  historyItem.historyType = SearchTypes.MANUAL_SYNTHESIS;
  if (payload) {
    payload.historyType = SearchTypes.MANUAL_SYNTHESIS;
  }
}

function setFiltering(payload, historyTypes, historyItem) {
  payload.historyType = historyTypes.FILTERED;
  payload.filters = historyItem.filters;
}

function setContextSwitch(payload, historyTypes) {
  payload.historyType = historyTypes.CONTEXT_SWITCH;
}

function setQuickSearch(search, historyTypes, historyItem, payload) {
  payload.quickSearchItem = {
    content: search.query || '',
    structure: historyItem.showStructure ? _getStructureFromSearch(search, payload.context) || '' : '',
    options: search.options || [],
    databases: UserDatabases.getListWithIds(),
  };
  if (historyItem.whereClauseImportToken) {
    payload.quickSearchItem.whereClauseImportToken = historyItem.whereClauseImportToken;
  }
  payload.historyType = historyTypes.QUICK_SEARCH;
}

function setBatchSearch(search, historyTypes, payload) {
  payload.historyType = historyTypes.BATCH_SEARCH;
  payload.batchFileName = search.batchFileName;
}

export function getHistoryTypes() {
  const historyTypes = AppStorage.instance.get('HistoryTypes');
  if (!historyTypes) {
    return HistoryResource.getHistoryTypes()
      .then((data) => {
        AppStorage.instance.set('HistoryTypes', data);
        return data;
      })
      .catch(Logger.error);
  }
  return Promise.resolve(historyTypes);
}

export function getSearchDescription(recentSearch) {
  const structures = extractStructuresFromFacts(recentSearch.queryFactItem);

  switch (recentSearch.historyType) {
    case 'quickSearch':
      recentSearch.comment = 'Quick Search: ';

      if (recentSearch.quickSearchItem) {
        defineQuickSearchDescription(recentSearch);
      }
      break;
    case 'filtered':
      recentSearch.comment = 'Filtered by: ';
      if (recentSearch.filters && recentSearch.filters.length) {
        defineFilteredDescription(recentSearch);
      }
      break;
    case 'contextSwitch':
      recentSearch.comment = 'Context Switch from: ';
      if (recentSearch.previousResult) {
        defineContextSwitchDescription(recentSearch);
      }
      break;
    case 'queryBuilder':
      recentSearch.comment = `Query Builder: in ${replaceCitations(recentSearch.context)} - `;
      if (recentSearch.queryFactItem) {
        recentSearch.structures = structures;
        defineFactDescription(recentSearch, recentSearch.queryFactItem);
      }
      break;
    case 'batchSearch':
      if (!recentSearch.comment) {
        recentSearch.comment = `Search from file "${recentSearch.batchFileName}": in ${replaceCitations(recentSearch.context)}`;
      }
      if (!recentSearch.description) {
        recentSearch.comment = `Search from file "${recentSearch.batchFileName}": in ${replaceCitations(recentSearch.context)}`;
      }
      break;
    case 'markush':
      recentSearch.comment = 'Markush structures from substances';
      break;
    case SearchTypes.MARKUSH_SUBSTANCES:
      recentSearch.comment = 'Substances from Markush structure';
      break;
    case 'manually':
      recentSearch.comment = 'Filtered by: manually selected items';
      break;
    case 'cross':
      recentSearch.comment = 'Database Switch from: ';
      if (recentSearch.previousResult) {
        defineContextSwitchDescription(recentSearch);
      }
      break;
    case 'similarity':
      recentSearch.structures = structures.filter((structure) => structure.options.some((option) => option.value === 'similarity'));
      recentSearch.comment = 'Similarity search by selected: ';
      break;
    case SearchTypes.MANUAL_SYNTHESIS:
      recentSearch.comment = 'From synthesize manually';
      break;
    default:
      break;
  }
  return recentSearch.comment;
}

export function formatHistoryData(history, historyTypes) {
  history.map(async (historyItem) => {
    getSearchDescription(historyItem);
    const search = await SearchStorage.get(historyItem.searchId);
    if (search && search.searchType && !historyItem.filters) {
      // when no filers but searchType specified, process depending on searchType
      if (search.searchType === SearchTypes.ADVANCED_SEARCH) {
        setQBSearch(search, historyTypes, historyItem);
      } else if (search.searchType === SearchTypes.SIMILARITY_SEARCH) {
        setSimilaritySearch(search, historyItem);
      } else if (search.searchType === SearchTypes.MARKUSH_SEARCH) {
        setMarkushSearch(historyItem);
      } else if (search.searchType === SearchTypes.MANUALLY_FILTERED) {
        setManuallyFiltered(historyItem);
      } else if (search.searchType === SearchTypes.CROSS_LINKING) {
        setCrossLinkingSearch(historyItem);
      } else if (search.searchType === SearchTypes.MARKUSH_SUBSTANCES) {
        setMarkushSubstancesSearch(historyItem);
      } else if (search.searchType === SearchTypes.MANUAL_SYNTHESIS) {
        setManualSynthesis(historyItem);
      } else {
        // when something is forgotten
        Logger.warn('Search type:', search.searchType, ' is not supported');
      }
    }
  });
}

function setDescriptionIfHasHistory(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS) {
  if (levelHasComplexHistory || fact.historyItems.length >= COMPLEX_HISTORY_ITEM_MIN_CHUNKS) {
    result.description += 'complex history item combination';
  } else {
    result.description += 'history item';
  }
}

function setDescriptionIfHasFields(fact, result) {
  each(fact.fields, (field, fieldIndex) => {
    if (fieldIndex > 0 && !isUndefined(field.fieldsLogicOperator)) {
      result.description += ' ' + field.fieldsLogicOperator;
      result.description += (field.fieldsLogicOperatorQualifier ? '{' + field.fieldsLogicOperatorQualifier + '}' : '') + ' ';
    }
    result.description += field.id + ' ' + (boundOperators[field.boundOperator] || '=') + ' "' + field.value + '"';
  });
}

function setDescriptionIfExist(fact, result) {
  result.description += ' ' + fact.id + ' exists';
}

export function getFormattedFactItems(factItem, result) {
  const COMPLEX_HISTORY_ITEM_MIN_CHUNKS = 3;

  const historyQLs = filter(factItem.facts, (fact) => {
    return fact.historyItems && fact.historyItems.length;
  });
  const levelHasComplexHistory = historyQLs.length >= COMPLEX_HISTORY_ITEM_MIN_CHUNKS;
  let isComplexHistoryItemAdded = false;

  each(factItem.facts, (fact, factIndex) => {
    if (fact.historyItems && fact.historyItems.length && isComplexHistoryItemAdded) {
      if (factIndex + 1 === factItem.facts.length) {
        result.description += ')';
      }

      return;
    }

    // Add opening bracket
    if (factIndex === 0) {
      // Prevent from unnecessary space symbol
      const lastDescLetter = result.description.slice(-1);
      if (lastDescLetter.match(/\W/g)) {
        result.description += '(';
      } else {
        result.description += ' (';
      }
    } else if (factIndex > 0 && !isUndefined(fact.logicOperator)) {
      result.description += ') ' + fact.logicOperator;
      result.description += (fact.logicOperatorQualifier ? '{' + fact.logicOperatorQualifier + '}' : '') + ' (';
    }

    // Fill contents of fact in description
    if (!isUndefined(fact.facts) && isUndefined(fact.fields)) {
      getFormattedFactItems(fact, result);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (fact.exist) {
        setDescriptionIfExist(fact, result);
      } else if (!isUndefined(fact.fields) && fact.fields.length) {
        setDescriptionIfHasFields(fact, result);
      } else if (fact.historyItems && fact.historyItems.length) {
        setDescriptionIfHasHistory(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS);

        if (levelHasComplexHistory) {
          isComplexHistoryItemAdded = true;
        }
      }
    }

    // Add closing bracket
    if (factIndex + 1 === factItem.facts.length) {
      result.description += ')';
    }
  });
}

export function formatNewHistory(historyItem, newSearchName) {
  return SearchStorage.get(historyItem.searchId)
    .then((search) => {
      return getHistoryTypes()
        .then((historyTypes) => {
          const payload = {
            databaseId: historyItem.databaseId,
            context: historyItem.context,
            hitSetKey: historyItem.hitSetKey,
            resultSize: historyItem.resultSize || historyItem.totalResult,
            userTime: moment().format('YYYY/MM/DD HH:mm:ss'),
            comment: historyItem.comment,
            searchName: newSearchName,
            resultName: historyItem.resultName,
            searchId: historyItem.searchId,
            order: historyItem.order,
            sort: historyItem.sort,
            totalResult: historyItem.totalResult,
            structure: _getStructureFromSearch(search, historyItem.context),
          };
          if (search && search.searchType && !historyItem.filters) {
            // when no filers but searchType specified, process depending on searchType
            if (search.searchType === SearchTypes.ADVANCED_SEARCH) {
              setQBSearch(search, historyTypes, historyItem, payload);
            } else if (search.searchType === SearchTypes.SIMILARITY_SEARCH) {
              setSimilaritySearch(search, historyItem, payload);
            } else if (search.searchType === SearchTypes.MARKUSH_SEARCH) {
              setMarkushSearch(historyItem, payload);
            } else if (search.searchType === SearchTypes.QUICK_SEARCH) {
              setQuickSearch(search, historyTypes, historyItem, payload);
            } else if (search.searchType === SearchTypes.BATCH_SEARCH) {
              setBatchSearch(search, historyTypes, payload);
            } else if (search.searchType === SearchTypes.MANUALLY_FILTERED) {
              setManuallyFiltered(historyItem, payload);
            } else if (search.searchType === SearchTypes.CROSS_LINKING) {
              setCrossLinkingSearch(historyItem, payload);
            } else if (search.searchType === SearchTypes.MARKUSH_SUBSTANCES) {
              setMarkushSubstancesSearch(historyItem, payload);
            } else if (search.searchType === SearchTypes.MANUAL_SYNTHESIS) {
              setManualSynthesis(historyItem, payload);
            } else {
              // when something is forgotten
              Logger.warn('Search type:', search.searchType, ' is not supported');
            }
          } else if (historyItem.filters) {
            // when filters, process as filtering
            setFiltering(payload, historyTypes, historyItem);
          } else if (!historyItem.historyType) {
            setContextSwitch(payload, historyTypes);
          }

          getSearchDescription(historyItem);
          return payload;
        })
        .catch(Logger.reject);
    })
    .catch(Logger.reject);
}

// eslint-disable-next-line default-param-last
export function composeFactDescription(comment = '', factItem) {
  each(factItem.facts, (fact, factIndex) => {
    if (!isUndefined(fact.facts) && isUndefined(fact.fields)) {
      composeFactDescription(comment, fact);
    } else {
      // for wrapped facts we need to put an operator
      if (factIndex === 0 && factItem.logicOperator) {
        comment += ` ${factItem.logicOperator}${factItem.logicOperatorQualifier ? '{' + factItem.logicOperatorQualifier + '}' : ''} `;
      }

      // facts need to be chained by logicOperator, so check if it is not the first field
      if (factIndex > 0 && fact.logicOperator) {
        comment += ` ${fact.logicOperator}${fact.logicOperatorQualifier ? '{' + fact.logicOperatorQualifier + '}' : ''} `;
      }

      if (!isUndefined(fact.fields) && fact.fields.length) {
        each(fact.fields, (field, fieldIndex) => {
          comment += field.displayName + ' "' + field.value + '"';

          // fields need to be chained by fieldsLogicOperator, so if it is not the last fact item chain it
          if (fieldIndex + 1 < fact.fields.length && !isUndefined(fact.fieldsLogicOperator)) {
            comment +=
              ' ' +
              fact.fieldsLogicOperator +
              (fact.fieldsLogicOperatorQualifier ? '{' + fact.fieldsLogicOperatorQualifier + '}' : '') +
              ' ';
          }
        });

        // facts need to be chained by logicOperator, so check if it is the last field
        if (factIndex + 1 === factItem.facts.length && !isUndefined(fact.logicOperator) && !isUndefined(factItem.structures)) {
          comment += ' ' + fact.logicOperator + (fact.logicOperatorQualifier ? '{' + fact.logicOperatorQualifier + '}' : '') + ' ';
        }
      } else if (!isUndefined(fact.advancedItems) && fact.advancedItems.length) {
        comment += `"${fact.advancedItems[0].advancedExpression}"`;
      }
    }
  });

  return comment;
}

export function copyQuery(query) {
  function done(queryVal) {
    appHistory.push({
      pathname: paths.ADVANCED,
      state: { importedQuery: queryVal },
    });
  }
  if (isObject(query.queryFactItem)) {
    done(query);
  } else {
    AdvancedQuery.getQueryByWhereClause(query)
      .then((whereClauseImport) => {
        if (whereClauseImport) {
          whereClauseImport.context = query.context;
          if (query.resolvedByCasApi) {
            whereClauseImport.resolvedByCasApi = query.resolvedByCasApi;
          }
          done(whereClauseImport);
        }
      })
      .catch(Logger.error);
  }
}

export function storeRecentHistory(activeBreadcrumb) {
  if (!activeBreadcrumb) return;

  const currentUser = AuthUtils.getUser();
  const { recentSearches } = currentUser.history;
  const crumbResultExists = find(recentSearches, { resultName: activeBreadcrumb.resultName });
  const crumbTimeExists = find(recentSearches, {
    creationTime: activeBreadcrumb.creationTime,
    databaseId: activeBreadcrumb.databaseId,
  });

  // add to recent only if not other exists with the same timestamp or resultName and skip history searches
  if (isUndefined(crumbTimeExists) && isUndefined(crumbResultExists) && !activeBreadcrumb.isHistoryBreadCrumb) {
    recentSearches.push(activeBreadcrumb);
    recentSearches.sort((x, y) => x.creationTime - y.creationTime);
    AuthUtils.updateUser({ history: currentUser.history });
  }
}
